/**
 * Interoperabilità con Google Maps (e simili), senza API a pagamento:
 *
 *  - parsing di coordinate incollate e di URL Google Maps completi
 *    (place, @centro, ?q=, /dir/A/B, !3d!4d, geo:, OpenStreetMap);
 *  - risoluzione best-effort degli short-link maps.app.goo.gl tramite
 *    proxy CORS pubblici gratuiti (il browser non può seguire i redirect
 *    cross-origin da solo);
 *  - costruzione dell'URL di export verso Google Maps con al massimo
 *    9 waypoint intermedi (limite dell'URL API), scelti con Douglas–Peucker.
 */

import type { LatLng } from './geo';
import { LocalProjection, simplifyToMaxInnerPoints } from './geo';

export type ParsedMapsInput =
  | { kind: 'point'; point: LatLng }
  | { kind: 'pair'; a: LatLng; b: LatLng }
  | { kind: 'dirQueries'; from: string; to: string }
  | { kind: 'query'; query: string }
  | { kind: 'shortlink'; url: string };

export class ShortLinkError extends Error {
  constructor(readonly userMessage: string) {
    super(userMessage);
    this.name = 'ShortLinkError';
  }
}

function validLatLng(lat: number, lng: number): LatLng | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * Interpreta coordinate incollate: "45.4642, 9.19", "45.4642 9.19",
 * e anche il formato italiano "45,4642, 9,1900".
 */
export function parseCoords(text: string): LatLng | null {
  const t = text.trim();
  // virgole decimali: due numeri "int,dec" separati da spazio/;/", "
  const it = t.match(/^(-?\d{1,3}),(\d+)\s*[,;\s]\s*(-?\d{1,3}),(\d+)$/);
  if (it) {
    return validLatLng(parseFloat(`${it[1]}.${it[2]}`), parseFloat(`${it[3]}.${it[4]}`));
  }
  const m = t.match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (m) return validLatLng(parseFloat(m[1]), parseFloat(m[2]));
  return null;
}

function cleanName(seg: string): string {
  return decodeURIComponent(seg.replace(/\+/g, ' ')).trim();
}

/** Ultima coppia !3d<lat>!4d<lng> presente nell'URL (pin del luogo). */
function fromBangs(url: string): LatLng | null {
  const re = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/g;
  let last: LatLng | null = null;
  for (const m of url.matchAll(re)) {
    const p = validLatLng(parseFloat(m[1]), parseFloat(m[2]));
    if (p) last = p;
  }
  return last;
}

/** Coppie !1d<lng>!2d<lat> (waypoint nei data-blob delle direction). */
function fromDirBangs(url: string): LatLng[] {
  const out: LatLng[] = [];
  for (const m of url.matchAll(/!1d(-?\d+(?:\.\d+)?)!2d(-?\d+(?:\.\d+)?)/g)) {
    const p = validLatLng(parseFloat(m[2]), parseFloat(m[1]));
    if (p) out.push(p);
  }
  return out;
}

function fromAt(url: string): LatLng | null {
  const m = url.match(/\/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?:[,/]|$)/);
  return m ? validLatLng(parseFloat(m[1]), parseFloat(m[2])) : null;
}

function isGoogleMapsHost(host: string): boolean {
  return (
    /(^|\.)google\.[a-z.]{2,10}$/.test(host) ||
    /^maps\.google\.[a-z.]{2,10}$/.test(host) ||
    host === 'maps.google.com'
  );
}

function isShortlinkHost(host: string, path: string): boolean {
  if (host === 'maps.app.goo.gl' || host === 'app.goo.gl') return true;
  if (host === 'goo.gl' && path.startsWith('/maps')) return true;
  return false;
}

function parseDirPath(path: string): ParsedMapsInput | null {
  const m = path.match(/\/maps\/dir\/(.*)$/);
  if (!m) return null;
  const rawSegs = m[1]
    .split('/')
    .filter((s) => s.length > 0 && !s.startsWith('@') && !s.startsWith('data='));
  const segs = rawSegs.map(cleanName).filter((s) => s.length > 0);
  if (segs.length === 0) return null;
  const coords = segs.map(parseCoords);
  const found = coords.filter((c): c is LatLng => c !== null);
  if (found.length >= 2) return { kind: 'pair', a: found[0], b: found[found.length - 1] };
  if (segs.length >= 2 && coords[0] === null && coords[segs.length - 1] === null) {
    return { kind: 'dirQueries', from: segs[0], to: segs[segs.length - 1] };
  }
  if (found.length === 1) return { kind: 'point', point: found[0] };
  return { kind: 'query', query: segs[0] };
}

export function parseMapsUrl(url: URL): ParsedMapsInput | null {
  const host = url.hostname.toLowerCase();
  const path = url.pathname;
  const full = url.toString();

  if (isShortlinkHost(host, path)) return { kind: 'shortlink', url: full };

  // OpenStreetMap: ?mlat/&mlon o #map=z/lat/lng
  if (/(^|\.)openstreetmap\.org$/.test(host)) {
    const mlat = parseFloat(url.searchParams.get('mlat') ?? '');
    const mlon = parseFloat(url.searchParams.get('mlon') ?? '');
    const marker = validLatLng(mlat, mlon);
    if (marker) return { kind: 'point', point: marker };
    const hm = url.hash.match(/map=\d+(?:\.\d+)?\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/);
    if (hm) {
      const p = validLatLng(parseFloat(hm[1]), parseFloat(hm[2]));
      if (p) return { kind: 'point', point: p };
    }
    return null;
  }

  if (!isGoogleMapsHost(host)) return null;

  // 1) direzioni: /maps/dir/…
  if (path.includes('/maps/dir/') || path.startsWith('/dir/')) {
    const dirWps = fromDirBangs(full);
    if (dirWps.length >= 2) {
      return { kind: 'pair', a: dirWps[0], b: dirWps[dirWps.length - 1] };
    }
    const byPath = parseDirPath(path.startsWith('/dir/') ? `/maps${path}` : path);
    if (byPath) return byPath;
  }

  // 2) pin del luogo (!3d!4d è più preciso del centro mappa)
  const pin = fromBangs(full);
  if (pin) return { kind: 'point', point: pin };

  // 3) parametri query espliciti
  for (const key of ['q', 'query', 'll', 'center', 'destination', 'daddr', 'saddr']) {
    const v = url.searchParams.get(key);
    if (!v) continue;
    const cleaned = v.replace(/^loc:/i, '').trim();
    const c = parseCoords(cleaned);
    if (c) return { kind: 'point', point: c };
    if (cleaned.length > 1 && !['q', 'll', 'center'].includes(cleaned)) {
      return { kind: 'query', query: cleaned.replace(/\+/g, ' ') };
    }
  }

  // 4) nome del luogo nel path
  const placeMatch = path.match(/\/maps\/(?:place|search)\/([^/@]+)/);
  if (placeMatch) {
    const name = cleanName(placeMatch[1]);
    const asCoords = parseCoords(name.replace(/\s*@.*$/, ''));
    if (asCoords) return { kind: 'point', point: asCoords };
    if (name.length > 1) {
      const at = fromAt(full);
      // il nome è più affidabile del centro mappa, ma se non geocodificabile
      // il chiamante può usare comunque il centro: privilegiamo il nome
      return at ? { kind: 'point', point: at } : { kind: 'query', query: name };
    }
  }

  // 5) centro mappa /@lat,lng,zoom
  const at = fromAt(full);
  if (at) return { kind: 'point', point: at };

  return null;
}

/** Interpreta qualsiasi testo incollato dall'utente. */
export function parseAnyInput(text: string): ParsedMapsInput | null {
  const t = text.trim();
  if (t.length === 0) return null;

  if (/^geo:/i.test(t)) {
    const m = t.match(/^geo:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);
    if (m) {
      const p = validLatLng(parseFloat(m[1]), parseFloat(m[2]));
      if (p) return { kind: 'point', point: p };
    }
    return null;
  }

  const urlMatch = t.match(/https?:\/\/\S+/);
  if (urlMatch) {
    try {
      const parsed = parseMapsUrl(new URL(urlMatch[0]));
      if (parsed) return parsed;
    } catch {
      /* URL non valido: si prova come testo */
    }
  }

  const coords = parseCoords(t);
  if (coords) return { kind: 'point', point: coords };

  if (t.length >= 2 && !t.startsWith('http')) return { kind: 'query', query: t };
  return null;
}

/** Estrae un riferimento Google Maps da un blob di testo/HTML (pagine proxy). */
export function extractFromText(text: string): ParsedMapsInput | null {
  const unescaped = text
    .replace(/\\u0026/g, '&')
    .replace(/\\u003d/g, '=')
    .replace(/&amp;/g, '&')
    .replace(/\\\//g, '/');

  const candidates: string[] = [];

  for (const m of unescaped.matchAll(
    /https:\/\/(?:www\.|maps\.)?google\.[a-z.]{2,10}\/maps[^"'<>\s\\]*/g,
  )) {
    candidates.push(m[0]);
  }
  for (const m of unescaped.matchAll(/[?&](?:continue|url|link)=([^&"'<>\s\\]+)/g)) {
    let v = m[1];
    for (let i = 0; i < 2; i++) {
      try {
        const dec = decodeURIComponent(v);
        if (dec === v) break;
        v = dec;
      } catch {
        break;
      }
    }
    if (v.startsWith('http')) candidates.push(v);
  }

  for (const c of candidates) {
    try {
      const parsed = parseMapsUrl(new URL(c));
      if (parsed && parsed.kind !== 'shortlink') return parsed;
    } catch {
      /* candidato malformato */
    }
  }

  // formato interno delle pagine Maps: APP_INITIALIZATION_STATE=[[[zoom,lng,lat
  const app = unescaped.match(
    /APP_INITIALIZATION_STATE=\[\[\[-?\d+(?:\.\d+)?,(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
  );
  if (app) {
    const p = validLatLng(parseFloat(app[2]), parseFloat(app[1]));
    if (p) return { kind: 'point', point: p };
  }

  return null;
}

export interface ShortLinkProxy {
  build: (target: string) => string;
  kind: 'allorigins' | 'raw';
}

export const SHORTLINK_PROXIES: ShortLinkProxy[] = [
  {
    build: (t) => `https://api.allorigins.win/get?url=${encodeURIComponent(t)}`,
    kind: 'allorigins',
  },
  { build: (t) => `https://corsproxy.io/?url=${encodeURIComponent(t)}`, kind: 'raw' },
];

const SHORTLINK_FAIL_MSG =
  'Non riesco a espandere questo link breve di Google Maps (i proxy gratuiti non rispondono). ' +
  'Apri il link nel browser e incolla qui l’indirizzo completo che appare nella barra, oppure le coordinate.';

/** Risolve un maps.app.goo.gl seguendo i redirect tramite proxy CORS gratuiti. */
export async function resolveShortLink(
  shortUrl: string,
  fetchImpl: typeof fetch = fetch,
  proxies: ShortLinkProxy[] = SHORTLINK_PROXIES,
): Promise<ParsedMapsInput> {
  for (const proxy of proxies) {
    try {
      const res = await fetchImpl(proxy.build(shortUrl), {
        signal: AbortSignal.timeout(12_000),
      });
      if (!res.ok) continue;
      let haystack = '';
      if (proxy.kind === 'allorigins') {
        const json = (await res.json()) as {
          contents?: string;
          status?: { url?: string };
        };
        haystack = `${json.status?.url ?? ''}\n${json.contents ?? ''}`;
      } else {
        haystack = `${res.url}\n${await res.text()}`;
      }
      const parsed = extractFromText(haystack);
      if (parsed) return parsed;
    } catch {
      /* si prova il proxy successivo */
    }
  }
  throw new ShortLinkError(SHORTLINK_FAIL_MSG);
}

const fmt = (v: number) => v.toFixed(6);

/** Massimo numero di waypoint intermedi ammessi dall'URL API di Google Maps. */
export const GMAPS_MAX_WAYPOINTS = 9;

/**
 * URL «Apri in Google Maps» per seguire il percorso calcolato:
 * origin/destination + fino a 9 waypoint scelti per preservare la forma.
 */
export function buildGmapsDirUrl(coords: LatLng[], waypoints?: LatLng[]): string {
  if (coords.length < 2) throw new Error('servono almeno 2 punti');
  let kept: LatLng[];
  if (waypoints && waypoints.length <= GMAPS_MAX_WAYPOINTS) {
    // waypoint minimi calcolati dal router: solo dove il percorso diverge dal piu' breve
    kept = [coords[0], ...waypoints, coords[coords.length - 1]];
  } else {
    // fallback geometrico: i punti che preservano la forma del tracciato
    const proj = new LocalProjection(coords[0]);
    const pts = coords.map((c) => proj.toXY(c));
    const keptIdx = simplifyToMaxInnerPoints(pts, GMAPS_MAX_WAYPOINTS);
    kept = keptIdx.map((i) => coords[i]);
  }

  const params = new URLSearchParams();
  params.set('api', '1');
  params.set('origin', `${fmt(kept[0].lat)},${fmt(kept[0].lng)}`);
  params.set('destination', `${fmt(kept[kept.length - 1].lat)},${fmt(kept[kept.length - 1].lng)}`);
  params.set('travelmode', 'walking');
  const inner = kept.slice(1, -1);
  if (inner.length > 0) {
    params.set('waypoints', inner.map((c) => `${fmt(c.lat)},${fmt(c.lng)}`).join('|'));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
